<script setup lang="ts">
import {computed, ref} from 'vue';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ShieldCheck,
  Stethoscope,
  UserRound
} from 'lucide-vue-next'

const step = ref(1), treatment = ref(''), practitioner = ref(''), date = ref(''), time = ref('');
const patient = ref({firstName: '', lastName: '', email: '', phone: '', privacy: false})
const treatments = [['Kontrolluntersuchung', '20 Min.'], ['Professionelle Zahnreinigung', '60 Min.'], ['Beratung Zahnersatz', '30 Min.'], ['Akute Beschwerden', '20 Min.']];
const practitioners = ['Dr. Anna Weber', 'Dr. Jonas Neumann', 'Keine Präferenz'];
const dates = ['Mo, 10. Aug.', 'Di, 11. Aug.', 'Mi, 12. Aug.', 'Do, 13. Aug.', 'Fr, 14. Aug.'];
const times = ['08:30', '09:10', '10:20', '11:40', '14:10', '15:30', '16:20'];
const canContinue = computed(() => step.value === 1 ? !!treatment.value : !!practitioner.value && !!date.value && !!time.value)
</script>
<template>
  <div>
    <section class="page-hero">
      <div class="container"><span class="eyebrow">Demo-Terminbuchung</span>
        <h1>Ihr Termin.<br><em>Einfach online gebucht.</em></h1>
        <p>Diese interaktive Demo speichert und überträgt keine persönlichen Daten.</p></div>
    </section>
    <section class="section booking-section">
      <div class="container booking-shell">
        <div class="booking-progress">
          <div v-for="n in 3" :key="n" :class="{active:step>=n}">
            <span>{{ step > n ? '✓' : n }}</span><small>{{ ['Behandlung', 'Termin', 'Ihre Daten'][n - 1] }}</small>
          </div>
        </div>
        <div v-if="step===1" class="booking-panel">
          <div class="panel-heading">
            <Stethoscope/>
            <div><h2>Was können wir für Sie tun?</h2>
              <p>Wählen Sie einen Besuchsgrund.</p></div>
          </div>
          <div class="choice-grid">
            <button v-for="item in treatments" :key="item[0]" :class="{selected:treatment===item[0]}"
                    @click="treatment=item[0]"><strong>{{ item[0] }}</strong><em>{{ item[1] }}</em></button>
          </div>
        </div>
        <div v-else-if="step===2" class="booking-panel">
          <div class="panel-heading">
            <CalendarDays/>
            <div><h2>Wann passt es Ihnen?</h2>
              <p>Alle Zeiten sind fiktive Demo-Termine.</p></div>
          </div>
          <h3 class="booking-label">Behandler</h3>
          <div class="practitioner-row">
            <button v-for="p in practitioners" :key="p" :class="{selected:practitioner===p}" @click="practitioner=p">
              <UserRound/>
              {{ p }}
            </button>
          </div>
          <h3 class="booking-label">Tag</h3>
          <div class="date-row">
            <button v-for="d in dates" :key="d" :class="{selected:date===d}" @click="date=d">{{ d }}</button>
          </div>
          <h3 class="booking-label">Uhrzeit</h3>
          <div class="time-grid">
            <button v-for="t in times" :key="t" :class="{selected:time===t}" @click="time=t">
              <Clock3/>
              {{ t }}
            </button>
          </div>
        </div>
        <form v-else-if="step===3" class="booking-panel" @submit.prevent="step=4">
          <div class="panel-heading">
            <UserRound/>
            <div><h2>Fast geschafft</h2>
              <p>Ergänzen Sie Ihre Kontaktdaten.</p></div>
          </div>
          <div class="booking-summary">
            <span><small>Behandlung</small><strong>{{ treatment }}</strong></span><span><small>Termin</small><strong>{{ date }} · {{ time }}</strong></span><span><small>Behandler</small><strong>{{ practitioner }}</strong></span>
          </div>
          <div class="field-row"><label><span>Vorname *</span><input v-model="patient.firstName"
                                                                     required></label><label><span>Nachname *</span><input
              v-model="patient.lastName" required></label></div>
          <div class="field-row"><label><span>E-Mail *</span><input v-model="patient.email" required
                                                                    type="email"></label><label><span>Telefon *</span><input
              v-model="patient.phone" required type="tel"></label></div>
          <label class="privacy-check"><input v-model="patient.privacy" required type="checkbox"><span>Ich akzeptiere die Demo-Datenschutzhinweise.</span></label>
          <button class="button primary booking-submit">
            <ShieldCheck/>
            Demo-Termin bestätigen
          </button>
        </form>
        <div v-else class="booking-success">
          <CheckCircle2/>
          <h2>Termin erfolgreich vorgemerkt</h2>
          <p>In einer echten Integration würde dieser Termin nun im Praxiskalender erscheinen und automatisch bestätigt
            werden.</p>
          <router-link to="/" class="button primary">Zur Startseite</router-link>
        </div>
        <div v-if="step<3" class="booking-actions">
          <button v-if="step>1" class="back-button" @click="step--">
            <ArrowLeft/>
            Zurück
          </button>
          <button class="button primary" :disabled="!canContinue" @click="step++">Weiter
            <ArrowRight/>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
